import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { Json, PipelineChampCustomRow } from '@/lib/database.types';

interface Props {
  champs: PipelineChampCustomRow[];
  values: Record<string, Json>;
  onChange: (champId: string, value: unknown) => void;
  /** ID de l'étape courante (filtre les champs avec visible_etapes) */
  etapeId: string | null;
}

/** Affiche les champs custom d'un pipeline, regroupés par section. */
export function ChampsCustomDynamiques({
  champs,
  values,
  onChange,
  etapeId,
}: Props): JSX.Element | null {
  const visibles = useMemo(
    () =>
      champs.filter((c) => {
        if (!c.visible_etapes || c.visible_etapes.length === 0) return true;
        return etapeId !== null && c.visible_etapes.includes(etapeId);
      }),
    [champs, etapeId],
  );

  const sections = useMemo(() => {
    const groups = new Map<string, PipelineChampCustomRow[]>();
    for (const c of visibles) {
      const key = c.section ?? 'Autres';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(c);
    }
    return Array.from(groups.entries());
  }, [visibles]);

  if (visibles.length === 0) return null;

  return (
    <section className="space-y-4">
      <header className="space-y-0.5 border-b border-border/60 pb-2">
        <h3 className="text-sm font-semibold leading-tight text-foreground">
          Champs spécifiques au pipeline
        </h3>
        <p className="text-xs text-muted-foreground">
          {visibles.length} champ{visibles.length > 1 ? 's' : ''} custom à renseigner.
        </p>
      </header>
      {sections.map(([section, chs]) => (
        <div key={section} className="space-y-2">
          {sections.length > 1 && (
            <h4 className="text-[10px] uppercase tracking-wide text-muted-foreground">{section}</h4>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            {chs.map((c) => (
              <ChampInput
                key={c.id}
                champ={c}
                value={values[c.id] ?? null}
                onChange={(v) => onChange(c.id, v)}
              />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

function ChampInput({
  champ,
  value,
  onChange,
}: {
  champ: PipelineChampCustomRow;
  value: Json | null;
  onChange: (v: unknown) => void;
}): JSX.Element {
  const id = `champc-${champ.id}`;
  const baseInput =
    'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1';
  const labelEl = (
    <Label htmlFor={id} className="text-xs">
      {champ.label}
      {champ.obligatoire && <span className="text-destructive"> *</span>}
    </Label>
  );

  if (champ.type === 'textarea')
    return (
      <div className="space-y-1.5 sm:col-span-2">
        {labelEl}
        <textarea
          id={id}
          rows={3}
          defaultValue={(value as string) ?? ''}
          onBlur={(e) => onChange(e.target.value || null)}
          className={cn(baseInput, 'min-h-[80px]')}
        />
      </div>
    );

  if (champ.type === 'boolean')
    return (
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        {champ.label}
        {champ.obligatoire && <span className="text-destructive"> *</span>}
      </label>
    );

  if (champ.type === 'select') {
    const options = (champ.options as string[] | null) ?? [];
    return (
      <div className="space-y-1.5">
        {labelEl}
        <select
          id={id}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          className={baseInput}
          required={champ.obligatoire}
        >
          <option value="">— Choisir —</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (champ.type === 'multiselect') {
    const options = (champ.options as string[] | null) ?? [];
    const arr = (value as string[] | null) ?? [];
    return (
      <div className="space-y-1.5">
        {labelEl}
        <div className="flex flex-wrap gap-1">
          {options.map((o) => {
            const active = arr.includes(o);
            return (
              <button
                key={o}
                type="button"
                onClick={() => onChange(active ? arr.filter((x) => x !== o) : [...arr, o])}
                className={cn(
                  'rounded-md border px-2 py-0.5 text-[11px]',
                  active
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-secondary',
                )}
              >
                {o}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const inputType =
    champ.type === 'number'
      ? 'number'
      : champ.type === 'date'
        ? 'date'
        : champ.type === 'datetime'
          ? 'datetime-local'
          : champ.type === 'url'
            ? 'url'
            : 'text';

  return (
    <div className="space-y-1.5">
      {labelEl}
      <input
        id={id}
        type={inputType}
        defaultValue={(value as string | number | null) ?? ''}
        onBlur={(e) => {
          const v = e.target.value;
          onChange(champ.type === 'number' ? (v === '' ? null : Number(v)) : v || null);
        }}
        className={baseInput}
        required={champ.obligatoire}
      />
    </div>
  );
}
